import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AlertService, AuthService } from '../services';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { MessageBoxComponent } from '@shared/components/dialogs/message-box/message-box.component';

@Injectable()
export class AccessDeniedInterceptor implements HttpInterceptor {
  private isHandlingTokenRevoked = false;
  private isHandlingAccountDisabled = false;

  constructor(
    private readonly _alertService: AlertService,
    private readonly _authService: AuthService,
    private readonly _router: Router,
    private readonly _toastrService: ToastrService,
    private readonly _translateService: TranslateService,
    private readonly _dialog: MatDialog,
  ) {}

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // Проверяем, является ли это ошибкой отключенного аккаунта
        if (this.isAccountDisabledError(error) && !this.isHandlingAccountDisabled) {
          this.isHandlingAccountDisabled = true;
          this.handleAccountDisabledError();
          return throwError(error);
        }

        // Обрабатываем ошибку 401 (Unauthorized) - недействительный токен
        if (error.status === 401) {
          // Проверяем, не является ли это ошибкой одновременной авторизации
          if (this.isTokenRevokedError(error) && !this.isHandlingTokenRevoked) {
            this.isHandlingTokenRevoked = true;
            this.handleTokenRevokedError();
            return throwError(error);
          }
        }

        // Обрабатываем ошибку 403 (Forbidden) - доступ запрещен
        if (error.status === 403) {
          if (
            !this.isTrialGameGenerationsLimitError(req, error) &&
            !this.isBlockedRegistrationError(req, error) &&
            !this.isSocialLinkError(req)
          ) {
            this._alertService.alertFail(error.error);
          }
        }
        return throwError(error);
      }),
    );
  }

  private isTrialGameGenerationsLimitError(
    request: HttpRequest<any>,
    error: HttpErrorResponse,
  ): boolean {
    return error.status === 403 && request.url.includes('v3/trial-period/game-generations');
  }

  private isBlockedRegistrationError(
    request: HttpRequest<any>,
    error: HttpErrorResponse,
  ): boolean {
    if (error.status !== 403) {
      return false;
    }

    const isRegistrationCreateRequest =
      request.method === 'POST' &&
      request.url.includes('/2/accounts/') &&
      request.url.includes('/create/');
    if (!isRegistrationCreateRequest) {
      return false;
    }

    if (!error.error || typeof error.error !== 'object') {
      return false;
    }

    const errorCode = (error.error as { code?: unknown }).code;
    return errorCode === 'blocked_registration';
  }

  private isSocialLinkError(request: HttpRequest<any>): boolean {
    if (!request.url.includes('/api/2/auth/')) {
      return false;
    }

    return (
      request.url.includes('/yandex/link/') ||
      request.url.includes('/telegram/link/') ||
      request.url.includes('/vk/link/')
    );
  }

  /**
   * Проверяет, является ли ошибка связанной с отозванным токеном
   */
  private isTokenRevokedError(error: HttpErrorResponse): boolean {
    if (error.error && typeof error.error === 'object') {
      const errorMessage = error.error.detail || error.error.message || '';
      return errorMessage === 'Token revoked';
    }
    return false;
  }
  /**
   * Проверяет, является ли ошибка связанной с отключенным аккаунтом
   */
  private isAccountDisabledError(error: HttpErrorResponse): boolean {
    if (error.error && typeof error.error === 'object') {
      const errorMessage = error.error.detail || error.error.message || '';
      return errorMessage === 'User account is disabled.';
    }
    return false;
  }

  /**
   * Обрабатывает ошибку отключенного аккаунта
   */
  private handleAccountDisabledError(): void {
    // Показываем диалоговое окно
    const dialogRef = this._dialog.open(MessageBoxComponent, {
      maxWidth: '600px',
      hasBackdrop: true,
      disableClose: true,
      data: {
        name: 'account-disabled',
        title: this._translateService.instant('baseToastr.warning'),
        text: this._translateService.instant('auth.accountDisabled.message'),
        isShowingDoNotShowAgain: false,
        links: [],
      },
    });

    // После закрытия диалога разлогиниваем и перенаправляем
    dialogRef.afterClosed().subscribe(() => {
      // Разлогиниваем пользователя
      this._authService.logout();

      // Перенаправляем на страницу логина
      this._router.navigate(['/login']).then(() => {
        this.isHandlingAccountDisabled = false;
      });
    });
  }

  /**
   * Обрабатывает ошибку отозванного токена
   */
  private handleTokenRevokedError(): void {
    this._toastrService.warning(
      this._translateService.instant('auth.moreDevices.loggin'),
      this._translateService.instant('baseToastr.warning'),
    );

    // Разлогиниваем пользователя
    this._authService.logout();

    // Перенаправляем на страницу логина
    this._router.navigate(['/login']);
  }
}

